import type { ErrorDetail, Locale } from '@sentra/contracts';
import { catalogueFor, isLocale, isMessageKey, placeholdersOf, translate } from '@sentra/contracts';

import { isApiError, UNFILLED_PLACEHOLDER } from '@/api/errors';

/**
 * The `details` a refusal carries, as the lines an operator reads under it.
 *
 * **The information was always on the wire and nothing rendered it.** `ErrorDetail` carries the path
 * that was refused and the message key that says why, and until this module the only consumer in the
 * application was the person form — and only for `body.fields.*`. Everywhere else a refusal arrived
 * as one sentence: for a `VALIDATION_FAILED`, "check the highlighted fields", with nothing
 * highlighted anywhere. That is the whole of what this fixes.
 *
 * **Nothing here trusts the wire.** `isApiErrorBody` validates the code and the message and does not
 * look at `details` at all, so `path` may not be a string and `messageKey` may name nothing; both
 * are checked here, and a detail that fails either is dropped rather than rendered as `undefined`.
 *
 * **A placeholder never reaches the screen.** The contracts catalogue leaves an unfilled `{{name}}`
 * standing on purpose, so that a missing value is visible rather than disguised as finished prose —
 * which is right for the build and wrong for somebody standing at a door. Every producer supplies
 * its values and `tools/i18n/schema-keys.ts` refuses one that does not; this is the net under that,
 * and it degrades the line rather than printing braces.
 */

/** One detail, ready to render. */
interface FailureDetail {
  /** The path exactly as it arrived, so a screen can attach it to a control of its own. */
  readonly path: string;
  /** What the line is about: a label the screen supplied, or the humanised path. */
  readonly subject: string;
  /** The sentence. Guaranteed to carry no standing placeholder. */
  readonly message: string;
  /** True when the message already names the subject, so a renderer must not prefix it again. */
  readonly namesSubject: boolean;
}

export interface FailureDetails {
  readonly shown: readonly FailureDetail[];
  /** How many the cap dropped, so a renderer can say so without counting. */
  readonly hidden: number;
}

/** A screen's own vocabulary: a human label for one of ITS paths, or `null`. */
export type DetailLabels = (path: string) => string | null;

/** The sentences this module needs and cannot import, for the reason `ClientMessages` gives. */
export interface DetailMessages {
  /** Between the machine segments of a humanised path. */
  readonly pathSeparator: string;
  readonly atIndex: (subject: string, index: string) => string;
  readonly inLanguage: (subject: string, language: string) => string;
  /** The name of a language, for a path segment that is one. */
  readonly languageName: (locale: Locale) => string;
}

export interface FailureDetailOptions {
  readonly labels?: DetailLabels;
  /** Paths a control has already taken, so the banner does not repeat what it says. */
  readonly consumed?: ReadonlySet<string>;
  readonly limit?: number;
}

/**
 * Five, because a banner is a summary rather than a report.
 *
 * Its counterpart is `MAX_REPORTED_COLLISIONS` in `passes/import.ts`, which is twenty: the batch
 * import is the one screen where the list IS the answer, and it raises the cap itself.
 */
const DEFAULT_DETAIL_LIMIT = 5;

/** The four sections a request carries input in. A path that starts with one is trimmed of it. */
const REQUEST_SECTIONS: ReadonlySet<string> = new Set(['body', 'query', 'params', 'headers']);

/** What separates one rendered detail from the next, inside the dedupe key. Never on screen. */
const IDENTITY_SEPARATOR = '\u0000';

/**
 * One path, as something a person can read, when no screen supplied a better name.
 *
 * **Fold, never translate.** A segment is a machine identifier — the same thing the register renders
 * in a monospace column beside every field, and the same thing the contracts catalogue itself prints
 * in "el campo «{{fieldPath}}» no existe". Inventing a Spanish word for `options` would be a word
 * neither catalogue owns. What IS localised is the punctuation and the two segments that genuinely
 * have a reading: an index, and a language tag.
 *
 * **The index is printed as it arrived, never `+1`.** `body.validation.options.1` is a zero-based
 * subscript and `serial.12` is a band serial that already counts from one, and nothing in the path
 * tells them apart. `[n]` reads unambiguously as a subscript and is never off by one about anything.
 */
export function humanisePath(path: string, messages: DetailMessages): string {
  const segments = segmentsOf(path);
  const body =
    segments.length > 1 && REQUEST_SECTIONS.has(segments[0] ?? '') ? segments.slice(1) : segments;
  return fold([], body, messages);
}

/** One path, as its segments, with a bracketed index read as one of them. */
function segmentsOf(path: string): string[] {
  return path
    .replaceAll(/\[(\w+)\]/gu, '.$1')
    .split('.')
    .filter((segment) => segment.length > 0);
}

/** The folding rule, shared, so a label the screen supplied gets its qualifiers too. */
function fold(
  started: readonly string[],
  segments: readonly string[],
  messages: DetailMessages,
): string {
  const parts = [...started];
  for (const segment of segments) {
    const previous = parts.at(-1);
    if (previous !== undefined && /^\d+$/u.test(segment)) {
      parts[parts.length - 1] = messages.atIndex(previous, segment);
    } else if (previous !== undefined && isLocale(segment)) {
      parts[parts.length - 1] = messages.inLanguage(previous, messages.languageName(segment));
    } else {
      parts.push(segment);
    }
  }
  return parts.join(messages.pathSeparator);
}

/**
 * What to call one path: a label the screen gave it, or the humanised form.
 *
 * The label is tried on the whole path and then on each shorter prefix, and whatever is left over is
 * folded onto it — so a settings screen that names `alerts.recipients` gets "Quién recibe los avisos
 * [0]" for `alerts.recipients.0` rather than losing the one part that says WHICH recipient.
 */
function subjectOf(
  path: string,
  labels: DetailLabels | undefined,
  messages: DetailMessages,
): string {
  if (labels !== undefined) {
    const segments = path.split('.');
    for (let length = segments.length; length > 0; length -= 1) {
      const found = labels(segments.slice(0, length).join('.'));
      if (found !== null) {
        return fold([found], segments.slice(length), messages);
      }
    }
  }
  return humanisePath(path, messages);
}

/** Whether a value off the wire is shaped like the detail the contract promises. */
function isRenderable(detail: unknown): detail is ErrorDetail {
  if (typeof detail !== 'object' || detail === null) {
    return false;
  }
  const candidate: { path?: unknown; messageKey?: unknown } = detail;
  return (
    typeof candidate.path === 'string' &&
    typeof candidate.messageKey === 'string' &&
    isMessageKey(candidate.messageKey)
  );
}

/** One detail, rendered in the operator's own language. */
function renderDetail(
  detail: ErrorDetail,
  locale: Locale,
  messages: DetailMessages,
  options: FailureDetailOptions,
): FailureDetail {
  const subject = subjectOf(detail.path, options.labels, messages);
  // `fieldKey` and `fieldPath` are the two schema-identifier placeholders on the allowlist, and this
  // side genuinely knows a better value for them: the server holds a machine key and two labels and
  // no idea which language the reader chose. `server-problems.ts` has always done exactly this.
  const rendered = translate(locale, detail.messageKey, {
    ...detail.params,
    fieldKey: subject,
    fieldPath: subject,
  });
  const wanted = placeholdersOf(catalogueFor(locale)[detail.messageKey]);
  return {
    path: detail.path,
    subject,
    // The net: a value only the server could have had leaves the line generic rather than braced.
    message: UNFILLED_PLACEHOLDER.test(rendered)
      ? translate(locale, 'errors.validation.unspecified')
      : rendered,
    namesSubject: wanted.includes('fieldKey') || wanted.includes('fieldPath'),
  };
}

/**
 * Every renderable detail of one failure, deduped and capped.
 *
 * Deduped because two things genuinely collapse onto one line: a batch import reports up to twenty
 * collisions under one path and one key, and two record keys too strange to repeat both report their
 * container. A reader shown the same sentence twice learns nothing from the second.
 */
export function failureDetails(
  error: unknown,
  locale: Locale,
  messages: DetailMessages,
  options: FailureDetailOptions = {},
): FailureDetails {
  if (!isApiError(error)) {
    return { shown: [], hidden: 0 };
  }
  const seen = new Set<string>();
  const rendered: FailureDetail[] = [];
  for (const detail of error.details) {
    if (!isRenderable(detail) || options.consumed?.has(detail.path) === true) {
      continue;
    }
    const one = renderDetail(detail, locale, messages, options);
    const identity = `${one.subject}${IDENTITY_SEPARATOR}${one.message}`;
    if (seen.has(identity)) {
      continue;
    }
    seen.add(identity);
    rendered.push(one);
  }
  const limit = options.limit ?? DEFAULT_DETAIL_LIMIT;
  return { shown: rendered.slice(0, limit), hidden: Math.max(0, rendered.length - limit) };
}
